import { Button, Card, Container, Grid, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import useResponsive from "../../minimals/hooks/useResponsive";
import { HeaderSlotContext } from "../components/layouts/dashboard/HeaderSlotContext";

type Prop = {
  section?: string;
  navigationUrlBuilder?: (orderNumber: string) => string;
  /** Shown in the mobile header beside the nav button. */
  title?: string;
};

// The component is shared by /confirmation, /searches, /notes and the insurance
// audit page, so the header title comes from the route rather than being hardcoded.
const SECTION_TITLES: Record<string, string> = {
  confirmation: "Confirmation",
  searches: "Searches",
  notes: "Notes"
};

export default function OrderNumberPrompt(prop: Prop) {
  const { section, navigationUrlBuilder, title } = prop;
  const navigate = useNavigate();
  const isMobile = useResponsive("down", "md");
  const { slotEl } = useContext(HeaderSlotContext);
  const [orderNumber, setOrderNumber] = useState<string>();
  const [submissionAttempted, setSubmissionAttempted] = useState<boolean>(false);
  const [invalidOrderNumber, setInvalidOrderNumber] = useState<boolean>(false);

  const headerTitle = title || (section ? SECTION_TITLES[section] : "") || "Order Number";

  const testOrderNumber = async () => {
    setSubmissionAttempted(true);
    const { data } = await axios({
      method: "get",
      url: `/proxy/api/orders/GetOrderInfo?orderNumber=${orderNumber}`
    });
    setInvalidOrderNumber(data === null);
  };

  useEffect(() => {
    if (submissionAttempted && !invalidOrderNumber) {
      if (!section && !navigationUrlBuilder) {
        return;
      }
      if (!orderNumber) {
        return;
      }
      const url = section ? `/${section}/${orderNumber}` : navigationUrlBuilder!(orderNumber);
      navigate(url);
    }
  }, [invalidOrderNumber, submissionAttempted]);

  const handleOrderNumberChange = (orderNumber: string) => {
    setSubmissionAttempted(false);
    setOrderNumber(orderNumber);
  };

  return (
    <>
      {/* Same pattern as /calendar and the phonebook: on mobile the page title sits
          in the site header beside the nav button instead of costing a page row. */}
      {isMobile && slotEl
        ? createPortal(
            <Typography
              noWrap
              sx={{ color: "primary.dark", fontSize: 17, fontWeight: 700, lineHeight: 1.2, minWidth: 0 }}
            >
              {headerTitle}
            </Typography>,
            slotEl
          )
        : null}

      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start"
        }}
      >
        {/* Was a fixed 600px, which overflowed the screen on a phone. */}
        <Card sx={{ width: "100%", maxWidth: 600 }}>
          <Grid container padding={{ xs: 2.5, md: 4 }} spacing={{ xs: 3, md: 4 }}>
            <Grid item xs={12}>
              <Typography>Please enter an order number to continue</Typography>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={2}>
                <TextField
                  error={submissionAttempted && invalidOrderNumber}
                  helperText={submissionAttempted && invalidOrderNumber && "Invalid order number"}
                  label="Order Number"
                  value={orderNumber}
                  onChange={(e) => handleOrderNumberChange(e.target.value)}
                />
                <Button
                  type="submit"
                  disabled={submissionAttempted}
                  variant="contained"
                  onClick={() => testOrderNumber()}
                >
                  {submissionAttempted ? "Submitting..." : "Continue"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </>
  );
}
